import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';

import { CONTAINER, EYEBROW, EYEBROW_MARK, H2, LEAD, SECTION_PAD } from '../classes';

interface QA {
  q: string;
  a: string;
}

const DETAILS = 'group isolate rounded-2xl border border-[#2C3B31] bg-[linear-gradient(180deg,rgb(15_21_16/.9),rgb(9_13_10/.92))] transition-colors open:border-[#93FF18]/[.38] has-[summary:focus-visible]:border-[#93FF18]/60 has-[summary:focus-visible]:shadow-[0_0_0_1px_rgb(147_255_24/.35),0_0_34px_-12px_rgb(147_255_24/.8)] forced-colors:border-[CanvasText]';
const SUMMARY = 'flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-[1rem] font-semibold text-white hover:text-[#93FF18] focus-visible:rounded-2xl focus-visible:shadow-none focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[-3px] focus-visible:outline-[#93FF18] forced-colors:focus-visible:outline-[Highlight] [&::-webkit-details-marker]:hidden [&_span]:[overflow-wrap:anywhere] leading-normal';
const PLUS = "relative size-[1.125rem] flex-none before:absolute before:left-1/2 before:top-1/2 before:h-0.5 before:w-[1.125rem] before:-translate-x-1/2 before:-translate-y-1/2 before:bg-[#93FF18] before:content-[''] after:absolute after:left-1/2 after:top-1/2 after:h-[1.125rem] after:w-0.5 after:-translate-x-1/2 after:-translate-y-1/2 after:bg-[#93FF18] after:transition after:duration-[240ms] after:content-[''] group-open:after:scale-y-0 group-open:after:opacity-0 forced-colors:before:bg-[CanvasText] forced-colors:after:bg-[CanvasText]";
const ANSWER = 'max-w-[44em] text-[.875rem] leading-[1.65] text-[#CBD5E1] [overflow-wrap:anywhere] max-sm:text-[.9375rem] [&_b]:font-semibold [&_b]:text-white';
const SUB_SUMMARY = "flex min-h-11 cursor-pointer list-none items-center gap-2 py-[.35rem] font-bold before:size-0 before:flex-none before:border-y-[.3em] before:border-s-[.5em] before:border-y-transparent before:border-s-current before:transition-transform before:content-[''] group-open:before:rotate-90 rtl:group-open:before:-rotate-90 [&::-webkit-details-marker]:hidden";

function asList<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export function Faq() {
  const { t } = useTranslation('pages/trial-matrix');
  const items = asList<QA>(t('lp.faq.items', { returnObjects: true }));
  const steps = asList<string>(t('lp.faq.steps', { returnObjects: true }));
  const extra = asList<QA>(t('lp.faq.extra', { returnObjects: true }));

  return (
    <section className={SECTION_PAD} id='faq' aria-labelledby='faq-t'>
      <div className={CONTAINER}>
        <div className='grid gap-[clamp(1.75rem,1rem+2.4vw,3rem)] lg:grid-cols-[minmax(0,min(22rem,38%))_minmax(0,1fr)] lg:gap-[clamp(2rem,1rem+3vw,4rem)]'>
          <div className='self-start' data-reveal=''>
            <p className={EYEBROW}>
              <span className={EYEBROW_MARK} aria-hidden='true'>
                &gt;
              </span>{' '}
              {t('lp.faq.eyebrow')}
            </p>
            <h2 className={H2} id='faq-t'>
              <Trans t={t} i18nKey='lp.faq.title' components={{ em: <em /> }} />
            </h2>
            <p className={LEAD}>{t('lp.faq.lead')}</p>
            <Link className='mt-5 inline-flex items-center gap-[.4rem] border-b border-[#93FF18]/40 pb-0.5 text-[.875rem] font-semibold leading-[1.4286] text-[#93FF18] hover:border-[#9CFF2E] hover:text-[#9CFF2E] focus-visible:rounded-xl [@media(pointer:coarse)]:min-h-11' href='/support' target='_blank' rel='noopener'>
              {t('lp.faq.support')}
              <span className='sr-only'>{t('lp.newTab')}</span>
            </Link>
          </div>

          <div className='grid content-start gap-2.5'>
            {items.map((item, index) => (
              <details key={item.q} className={DETAILS} open={index === 0}>
                <summary className={SUMMARY}>
                  <span>{item.q}</span>
                  <i className={PLUS} aria-hidden='true' />
                </summary>
                <div className='px-5 pb-5'>
                  <p className={ANSWER}>
                    <Trans t={t} i18nKey={`lp.faq.items.${index}.a`} components={{ b: <b /> }} />
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>

        <div className='mx-auto my-8 max-w-[1120px] rounded-2xl border border-[rgb(110_226_220/.28)] bg-[rgb(8_28_43/.85)] p-6 text-[#eafaff]' role='region' aria-label={t('lp.faq.guideLabel')}>
          <h3 className='mb-4 mt-[.1rem] text-[1.35rem] text-[#9ff3e8]'>{t('lp.faq.guideTitle')}</h3>
          <ol className='list-decimal ps-[1.6rem] [&_li]:my-[.48rem] [&_li]:leading-[1.55]'>
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          {extra.map((item) => (
            <details key={item.q} className='group mt-[.7rem] border-t border-white/15 pt-[.7rem]'>
              <summary className={SUB_SUMMARY}>{item.q}</summary>
              <p className='leading-[1.6]'>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
